import React, { useEffect } from 'react';
import Icon from './Icon';
import styles from './Modal.module.css';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  /** Accessible label for the close button. Defaults to the Spanish "Cerrar modal". */
  closeLabel?: string;
  /** Optional structural footer (e.g. action buttons). Rendered below children,
   *  right-aligned with a top divider. Omit for modals without actions. */
  footer?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  className = '',
  closeLabel = 'Cerrar modal',
  footer,
}) => {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={`${styles.modal} uc-modal open`}
      style={{ display: 'block' }}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div className={`uc-modal_dialog ${styles.dialog} ${styles[size]}`}>
        <div className={`uc-modal_content uc-message ${styles.content} ${className}`}>
          <div className={`uc-message_body ${styles.body}`}>
            {title ? (
              <div className={styles.header}>
                <h2 id="modal-title" className={styles.title}>
                  {title}
                </h2>
                <button
                  className={styles.closeButton}
                  onClick={onClose}
                  aria-label={closeLabel}
                >
                  <Icon name="close" />
                </button>
              </div>
            ) : (
              <div className={styles.headerNoTitle}>
                <button
                  className={styles.closeButton}
                  onClick={onClose}
                  aria-label={closeLabel}
                >
                  <Icon name="close" />
                </button>
              </div>
            )}
            <div className={styles.children}>{children}</div>
            {footer && <div className={styles.footer}>{footer}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
